/**
 * MethodCard - Displays a single get started method option
 *
 * A clickable card that presents one option for populating data.
 * Features hover effects and visual feedback for interactivity.
 *
 * @template T - Type of method ID (defaults to string for flexibility)
 */

import { ChevronRightIcon } from '@heroicons/react/24/outline';

import { cn } from '@/utils/classname';

import type { MethodCardProps } from './types';

export function MethodCard<T extends string = string>({
  method,
  onClick,
}: MethodCardProps<T>) {
  const Icon = method.icon;

  return (
    <button
      onClick={onClick}
      className={cn(
        'group flex flex-col items-start gap-3 rounded-lg border p-4 pb-6 text-left transition-colors',
        'border-chariot-stroke bg-chariot-background-secondary',
        'hover:border-chariot-brand-primary hover:bg-chariot-background-tertiary'
      )}
      data-testid={`get-started-method-${method.id}`}
    >
      <div className="bg-chariot-brand-primary/10 rounded-md p-2">
        <Icon className="text-chariot-brand-primary size-6" />
      </div>
      <div className="flex flex-col gap-1">
        <h3 className="text-chariot-text-primary font-semibold">
          {method.title}
        </h3>
        <p className="text-chariot-text-secondary text-sm">
          {method.description}
        </p>
      </div>
      <div
        className={cn(
          'mt-auto inline-flex items-center gap-1 rounded px-3 py-1.5 text-sm font-semibold transition-colors',
          method.buttonStyle === 'primary'
            ? 'bg-chariot-brand-primary group-hover:bg-chariot-brand-secondary text-white'
            : 'border-chariot-stroke bg-chariot-background-tertiary text-chariot-text-primary group-hover:border-chariot-brand-primary border'
        )}
      >
        {method.buttonLabel}
        <ChevronRightIcon className="size-4 transition-transform group-hover:translate-x-0.5" />
      </div>
    </button>
  );
}
